import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import { useConfirm } from "@/components/ConfirmDialog";
import { useUrlState } from "@/lib/useUrlState";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RiskBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PhoneCall, PhoneSlash, Plus, PencilSimple,
  Trash, Clock, Note, UsersThree, Buildings,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CallLogDialog from "@/components/CallLogDialog";
import ActivityFilters from "@/components/ActivityFilters";
import ActivityHowToUse from "@/components/ActivityHowToUse";

const PAGE_SIZE = 50;

export default function Activity() {
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const isPoc = me?.role === "poc";
  const isSuperAdmin = me?.role === "super_admin";

  const [dateFrom, setDateFrom] = useUrlState("from", "");
  const [dateTo, setDateTo] = useUrlState("to", "");
  const [filterPoc, setFilterPoc] = useUrlState("poc", "all");
  const [filterFirm, setFilterFirm] = useUrlState("firm", "");
  const [filterType, setFilterType] = useUrlState("type", "all");
  const [filterTier, setFilterTier] = useUrlState("tier", "all");
  const [filterRisk, setFilterRisk] = useUrlState("risk", "all");
  const [filterCategory, setFilterCategory] = useUrlState("category", "all");
  const [filterLoggedBy, setFilterLoggedBy] = useUrlState("by", "all");
  const [page, setPage] = useState(1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [pocOptions, setPocOptions] = useState([]);
  const [loggerOptions, setLoggerOptions] = useState([]);

  useEffect(() => {
    api.get("/meta/options").then((r) => {
      setPocOptions(r.data.pocs || []);
      setLoggerOptions(r.data.loggers || []);
    });
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await api.get("/activity", {
        params: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          poc: filterPoc !== "all" ? filterPoc : undefined,
          firm: filterFirm || undefined,
          type: filterType !== "all" ? filterType : undefined,
          tier: filterTier !== "all" ? filterTier : undefined,
          risk: filterRisk !== "all" ? filterRisk : undefined,
          category: filterCategory !== "all" ? filterCategory : undefined,
          logged_by: filterLoggedBy !== "all" ? filterLoggedBy : undefined,
          page,
          page_size: PAGE_SIZE,
        },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, filterPoc, filterFirm, filterType, filterTier, filterRisk, filterCategory, filterLoggedBy, page]);

  // Group rows by occurred-at date for sticky daily sections. Daily total now
  // sums BOTH call durations (talk-time) and note durations (work-time) — POCs
  // log work-done as notes with durations.
  const groupedRows = useMemo(() => {
    const rows = data?.rows || [];
    const byDay = new Map();
    for (const row of rows) {
      const d = new Date(row.occurred_at);
      if (Number.isNaN(d.getTime())) continue;
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      let g = byDay.get(dateKey);
      if (!g) {
        g = { dateKey, dateLabel: formatDayLabel(d), totalMinutes: 0, rows: [] };
        byDay.set(dateKey, g);
      }
      g.totalMinutes += row.duration_minutes || 0;
      g.rows.push(row);
    }
    return Array.from(byDay.values());
  }, [data]);

  const clearFilters = () => {
    // Reset ALL filter URL params in a single setSearchParams call. Calling
    // each useUrlState setter sequentially would stomp on each other within
    // the same tick (each setter reads from the same stale `params` snapshot,
    // so only the last write survives).
    setSearchParams({}, { replace: true });
    setPage(1);
  };

  const remove = async (row) => {
    const noun = row.entry_type === "note" ? "note" : "log";
    if (!await confirm({
      title: `Delete this ${noun}?`,
      body: "This cannot be undone.",
      confirmLabel: `Delete ${noun}`,
      destructive: true,
    })) return;
    try {
      const url = row.entry_type === "note"
        ? `/notes/${row.entry_id}`
        : `/call-logs/${row.entry_id}`;
      await api.delete(url);
      toast.success(`${noun.charAt(0).toUpperCase()}${noun.slice(1)} deleted`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filtersDirty = (
    dateFrom || dateTo || filterPoc !== "all" || filterFirm ||
    filterType !== "all" || filterTier !== "all" || filterRisk !== "all" ||
    filterCategory !== "all" || filterLoggedBy !== "all"
  );

  return (
    <div className="space-y-3 h-[calc(100vh-110px)] flex flex-col">
      {/* 1. Title + subtitle in one 36px line + Log button */}
      <div className="flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <PhoneCall size={22} weight="fill" className="text-violet-600 shrink-0" />
          <h1 className="font-display text-2xl font-semibold tracking-tight text-gray-900">Activity</h1>
          <span className="text-gray-300">·</span>
          <p className="text-sm text-gray-500 truncate">
            {isPoc
              ? "Your calls and notes, plus admin entries on your firms. Edit within 30 days."
              : "Every call and note logged across the org. Filter, drill down, export."}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ActivityHowToUse />
          <Button onClick={() => setShowAdd(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="act-add-btn">
            <Plus size={16} className="mr-1.5" />
            Log a call/note
          </Button>
        </div>
      </div>

      {/* Filter bar + Export — extracted into <ActivityFilters> */}
      <ActivityFilters
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        filterPoc={filterPoc} setFilterPoc={setFilterPoc}
        filterLoggedBy={filterLoggedBy} setFilterLoggedBy={setFilterLoggedBy}
        filterType={filterType} setFilterType={setFilterType}
        filterTier={filterTier} setFilterTier={setFilterTier}
        filterRisk={filterRisk} setFilterRisk={setFilterRisk}
        filterCategory={filterCategory} setFilterCategory={setFilterCategory}
        filterFirm={filterFirm} setFilterFirm={setFilterFirm}
        pocOptions={pocOptions}
        loggerOptions={loggerOptions}
        isPoc={isPoc}
        filtersDirty={filtersDirty}
        clearFilters={clearFilters}
        pageReset={() => setPage(1)}
        total={data?.total ?? 0}
      />

      {/* 10. Table fills remaining height (flex-1) — sticks just above the Made-with-Emergent badge */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col flex-1 min-h-0" data-testid="act-table">
        {/* Vertical scroll only. `scrollbar-gutter: stable` reserves the scrollbar
            track inside the container's border-box — so the container's bg paints
            all the way to the true right edge (no white gap next to the scrollbar)
            and the scrollbar never overlaps the actions column icons. */}
        <div
          className="overflow-y-auto overflow-x-hidden flex-1"
          style={{ scrollbarGutter: "stable" }}
        >
          <table className="w-full text-sm table-fixed">
            <thead className="bg-gray-50 text-left sticky top-0 z-10">
              <tr>
                <Th className="w-[90px]">Time</Th>
                <Th className="w-[48px] text-center">Type</Th>
                <Th className="w-[20%]">Firm</Th>
                <Th className="w-[125px]">Risk</Th>
                <Th className="w-[120px]">Logged by</Th>
                {/* Remark absorbs all remaining space; row height stays fixed via truncate. */}
                <Th>Remark</Th>
                <Th className="w-[74px] text-right">Duration</Th>
                <Th className="w-[72px] text-right pr-3"></Th>
              </tr>
            </thead>
            {loading && (
              <tbody><tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">Loading…</td></tr></tbody>
            )}
            {!loading && (data?.rows?.length ?? 0) === 0 && (
              <tbody><tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">
                No activity yet. Click "Log a call/note" to add the first entry.
              </td></tr></tbody>
            )}
            {!loading && (data?.rows?.length ?? 0) > 0 && groupedRows.map((group) => (
              <tbody key={group.dateKey} data-testid={`act-group-${group.dateKey}`}>
                {/* Group header sticks *just underneath* the main column header.
                    `top-[35px]` = 1px overlap into the 36px column-header row, which
                    reliably hides any sub-pixel gap where partial row content used to
                    show through while scrolling. Main header keeps z-10 so it always
                    paints on top of this row. */}
                <tr className="bg-gray-50 border-b border-gray-200 sticky top-[35px] z-[5]">
                  <td colSpan={8} className="px-4 py-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-gray-800 font-display" data-testid={`act-group-label-${group.dateKey}`}>
                          {group.dateLabel}
                        </span>
                        <span className="text-[11px] text-gray-500 font-mono">
                          · {group.rows.length} entr{group.rows.length === 1 ? "y" : "ies"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[12px] text-violet-700 font-semibold" data-testid={`act-group-total-${group.dateKey}`}>
                        <Clock size={13} weight="bold" />
                        {fmtDuration(group.totalMinutes) || "0m"}
                      </div>
                    </div>
                  </td>
                </tr>
                {group.rows.map((row) => {
                  const ownEntry = row.logged_by_email === me?.email;
                  const isExpanded = expanded === row.entry_id;
                  const within30d = (new Date() - new Date(row.created_at)) < 30 * 24 * 3600 * 1000;
                  const canEdit = ownEntry && within30d;
                  const canDelete = canEdit || isSuperAdmin;
                  const wasEdited = row.updated_at && row.updated_at !== row.created_at;
                  const firstName = (row.logged_by_name || "").trim().split(/\s+/)[0] || row.logged_by_name;
                  const isInternal = row.target_type === "internal";
                  const isGeneral = row.entry_type === "note" && row.target_type === "general";
                  const isTrial = row.target_type === "trial";
                  const noFirm = isInternal || isGeneral || isTrial;
                  // POCs can log calls/notes on any firm for backup coverage, but
                  // reading another POC's Firm 360 is firewalled. So make the firm
                  // name plain text when the row isn't in the POC's scope (neither
                  // support nor sales POC of the firm).
                  const myPocNames = me?.poc_names || (me?.poc_name ? [me.poc_name] : []);
                  const firmInScope =
                    !isPoc ||
                    (row.firm_poc && myPocNames.includes(row.firm_poc)) ||
                    (row.firm_sales_poc && myPocNames.includes(row.firm_sales_poc));
                  return (
                    <tr key={row.entry_id}
                      className="group border-b border-gray-100 hover:bg-gray-50/50 transition-colors cursor-pointer"
                      onClick={() => setExpanded(isExpanded ? null : row.entry_id)}
                      data-testid={`act-row-${row.entry_id}`}>
                      {/* 7. py-3 → py-2 — row drops from ~110px to ~64px */}
                      <td className="px-4 py-2 whitespace-nowrap align-top">
                        <div className="text-sm text-gray-700 font-mono">{formatTime(row.occurred_at)}</div>
                      </td>
                      <td className="px-4 py-2 text-center align-top"><TypeIcon label={row.type_label} /></td>
                      {/* Firm name is the ONLY click target that navigates to Firm 360.
                          stopPropagation keeps the rest of the row's "click to expand
                          remark" behaviour intact. No-firm rows (internal calls /
                          general notes) render an inert chip instead. */}
                      <td className="px-4 py-2 align-top min-w-0 max-w-0">
                        {isInternal ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[11px] uppercase tracking-wider font-semibold text-slate-700"
                            data-testid={`act-firm-internal-${row.entry_id}`}
                          >
                            <UsersThree size={11} weight="fill" />
                            Internal call
                          </span>
                        ) : isGeneral ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[11px] uppercase tracking-wider font-semibold text-amber-700"
                            data-testid={`act-firm-general-${row.entry_id}`}
                          >
                            <Note size={11} weight="fill" />
                            General note
                          </span>
                        ) : isTrial ? (
                          <div
                            className="min-w-0 max-w-full leading-tight"
                            data-testid={`act-firm-trial-${row.entry_id}`}
                          >
                            <div className="font-medium text-gray-900 truncate" title={row.trial_firm_name}>
                              {row.trial_firm_name || "—"}
                            </div>
                            <div className="text-[10px] text-gray-400 truncate font-mono" title={row.trial_firm_url}>
                              {row.trial_firm_url || ""}
                            </div>
                          </div>
                        ) : firmInScope ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/firms/${row.firm_subdomain}${firmFromQS(location)}`);
                            }}
                            className="font-medium text-gray-900 truncate hover:text-violet-700 hover:underline text-left block max-w-full leading-tight"
                            title={row.firm_name}
                            data-testid={`act-firm-link-${row.entry_id}`}
                          >
                            {row.firm_name}
                          </button>
                        ) : (
                          <span
                            className="font-medium text-gray-700 inline-flex items-center gap-1.5 max-w-full leading-tight min-w-0"
                            title={`${row.firm_name} · backup coverage — owned by ${row.firm_poc || "—"}`}
                            data-testid={`act-firm-readonly-${row.entry_id}`}
                          >
                            <Buildings size={11} className="text-gray-400 shrink-0" />
                            <span className="truncate">{row.firm_name}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 align-top">
                        {noFirm ? <span className="text-xs text-gray-300">—</span> : <RiskBadge risk={row.firm_risk} />}
                      </td>
                      {/* 9. Logged-by: first name only; full name + email on hover (title) */}
                      <td className="px-4 py-2 align-top">
                        <span
                          className="text-sm text-gray-900 truncate cursor-help"
                          title={`${row.logged_by_name}\n${row.logged_by_email}`}
                          data-testid={`act-loggedby-${row.entry_id}`}
                        >
                          {firstName}
                        </span>
                      </td>
                      {/* 5+8. Remark gets the freed-up space. Collapsed = single-line
                          truncate with ellipsis so the row height stays fixed AND the
                          table never widens; click the row to expand into wrap. */}
                      <td className="px-4 py-2 align-top min-w-0 max-w-0">
                        <div
                          className={`text-sm ${row.remark ? "text-gray-700" : "text-gray-300 italic"} ${isExpanded ? "whitespace-pre-wrap break-words" : "truncate"}`}
                          title={row.remark || ""}
                          data-testid={`act-remark-${row.entry_id}`}
                        >
                          {row.remark || "—"}
                          {wasEdited && (
                            <span className="text-[11px] text-gray-400 italic ml-1">· edited</span>
                          )}
                        </div>
                      </td>
                      {/* 4. Duration column tightened from w-24 → w-16 */}
                      <td className="px-2 py-2 text-right whitespace-nowrap align-top overflow-hidden">
                        {row.duration_minutes ? (
                          <span className="inline-flex items-center gap-1 text-sm font-mono text-gray-800" data-testid={`act-duration-${row.entry_id}`}>
                            <Clock size={11} className="text-gray-400 shrink-0" />
                            {fmtDuration(row.duration_minutes)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-1 py-2 pr-3 text-right whitespace-nowrap align-top" onClick={(e) => e.stopPropagation()}>
                        {/* Actions fade in on row hover so they don't take visible space
                            when idle — the narrow column stays reserved regardless.
                            Buttons are square + zero-padding so both fit inside the 56px
                            column edge-to-edge without clipping. */}
                        <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity inline-flex items-center gap-0.5">
                          {canEdit && (
                            <Button size="sm" variant="ghost" onClick={() => setEditing(row)} className="h-7 w-7 p-0" data-testid={`act-edit-${row.entry_id}`}>
                              <PencilSimple size={13} />
                            </Button>
                          )}
                          {canDelete && (
                            <Button size="sm" variant="ghost" onClick={() => remove(row)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`act-delete-${row.entry_id}`}>
                              <Trash size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
        {data?.pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs shrink-0">
            <div className="text-gray-500">Page {data.page} of {data.pages}</div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="act-prev-page">Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="act-next-page">Next</Button>
            </div>
          </div>
        )}
      </div>

      <CallLogDialog
        open={showAdd || !!editing}
        editing={editing}
        onOpenChange={(v) => { if (!v) { setShowAdd(false); setEditing(null); } }}
        onSaved={() => { setShowAdd(false); setEditing(null); fetchRows(); }}
      />
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`h-9 px-4 text-[11px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200 ${className}`}>{children}</th>;
}

function TypeIcon({ label }) {
  if (label === "picked_up") {
    return (
      <span title="Picked up" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid="type-picked-up">
        <PhoneCall size={13} weight="fill" />
      </span>
    );
  }
  if (label === "no_answer") {
    return (
      <span title="Didn't pick up" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-50 border border-amber-200 text-amber-700" data-testid="type-no-answer">
        <PhoneSlash size={13} weight="fill" />
      </span>
    );
  }
  if (label === "internal_call") {
    return (
      <span title="Internal call" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 border border-slate-300 text-slate-700" data-testid="type-internal">
        <UsersThree size={13} weight="fill" />
      </span>
    );
  }
  if (label === "general_note") {
    return (
      <span title="General note" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-50 border border-amber-200 text-amber-700" data-testid="type-general-note">
        <Note size={13} weight="fill" />
      </span>
    );
  }
  if (label === "trial_call") {
    return (
      <span title="Trial firm call" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid="type-trial-call">
        <PhoneCall size={13} weight="fill" />
      </span>
    );
  }
  if (label === "trial_note") {
    return (
      <span title="Trial firm note" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-50 border border-violet-200 text-violet-700" data-testid="type-trial-note">
        <Note size={13} weight="fill" />
      </span>
    );
  }
  return (
    <span title="Note" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-50 border border-violet-200 text-violet-700" data-testid="type-note">
      <Note size={13} weight="fill" />
    </span>
  );
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function formatDayLabel(d) {
  const today = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(today) - startOfDay(d)) / (24 * 3600 * 1000));
  if (diffDays === 0) return `Today, ${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`;
  if (diffDays === 1) return `Yesterday, ${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

function fmtDuration(total) {
  if (!total || total <= 0) return "";
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}
